/* =========================================================================
   WMP Modal — Shared modal shell component
   ========================================================================= */
(function () {
  'use strict';

  var backdrop = null;
  var dialog = null;
  var startFocusGuard = null;
  var endFocusGuard = null;
  var closeBtn = null;
  var titleEl = null;
  var contentEl = null;
  var actionsEl = null;
  var primaryBtn = null;
  var primaryActionCb = null;
  var onCloseCb = null;
  var previousFocus = null;
  var hiddenSiblings = [];
  var built = false;

  /* -----------------------------------------------------------------------
     Build DOM (once, reused)
     ----------------------------------------------------------------------- */
  function build() {
    if (built) return;

    var root = document.getElementById('wmp-prescription-flow');
    if (!root) return;

    // Backdrop
    backdrop = document.createElement('div');
    backdrop.className = 'wmp-modal-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) close();
    });

    // Dialog
    dialog = document.createElement('div');
    dialog.className = 'wmp-modal-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('tabindex', '-1');

    startFocusGuard = document.createElement('button');
    startFocusGuard.className = 'wmp-modal__focus-guard';
    startFocusGuard.setAttribute('type', 'button');
    startFocusGuard.setAttribute('aria-label', 'Return to the end of this dialog');
    startFocusGuard.setAttribute('data-wmp-modal-focus-guard', 'start');
    startFocusGuard.addEventListener('focus', focusLastElement);
    startFocusGuard.addEventListener('click', focusLastElement);
    dialog.appendChild(startFocusGuard);

    // Close button
    closeBtn = document.createElement('button');
    closeBtn.className = 'wmp-modal__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
      '<path d="M14 4L4 14M4 4L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    closeBtn.addEventListener('click', close);
    dialog.appendChild(closeBtn);

    // Title
    titleEl = document.createElement('h3');
    titleEl.className = 'wmp-modal__title';
    titleEl.id = 'wmp-modal-title';
    titleEl.setAttribute('tabindex', '-1');
    dialog.appendChild(titleEl);

    // Content
    contentEl = document.createElement('div');
    contentEl.className = 'wmp-modal__content';
    contentEl.id = 'wmp-modal-content';
    dialog.appendChild(contentEl);

    // Footer actions
    actionsEl = document.createElement('div');
    actionsEl.className = 'wmp-modal__actions';
    actionsEl.hidden = true;

    primaryBtn = document.createElement('button');
    primaryBtn.className = 'wmp-btn wmp-btn--primary wmp-btn--xl wmp-btn--full';
    primaryBtn.setAttribute('type', 'button');
    primaryBtn.addEventListener('click', function () {
      if (typeof primaryActionCb === 'function') {
        primaryActionCb();
        return;
      }
      close();
    });
    actionsEl.appendChild(primaryBtn);
    dialog.appendChild(actionsEl);

    endFocusGuard = document.createElement('button');
    endFocusGuard.className = 'wmp-modal__focus-guard';
    endFocusGuard.setAttribute('type', 'button');
    endFocusGuard.setAttribute('aria-label', 'Return to the close dialog button');
    endFocusGuard.setAttribute('data-wmp-modal-focus-guard', 'end');
    endFocusGuard.addEventListener('focus', focusFirstElement);
    endFocusGuard.addEventListener('click', focusFirstElement);
    dialog.appendChild(endFocusGuard);

    backdrop.appendChild(dialog);
    root.appendChild(backdrop);
    built = true;
  }

  /* -----------------------------------------------------------------------
     Focus trap
     ----------------------------------------------------------------------- */
  function getFocusable() {
    if (!dialog) return [];
    var elements = Array.prototype.slice.call(
      dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    );

    return elements.filter(function (element) {
      if (!element || element.hasAttribute('data-wmp-modal-focus-guard')) return false;
      if (element.disabled) return false;
      if (element.closest('[hidden], [aria-hidden="true"]')) return false;

      var styles = window.getComputedStyle ? window.getComputedStyle(element) : null;
      if (styles && (styles.display === 'none' || styles.visibility === 'hidden')) return false;

      return element.getClientRects().length > 0;
    });
  }

  function focusElement(element) {
    if (!element || typeof element.focus !== 'function') return;

    try {
      element.focus({ preventScroll: true });
    } catch (e) {
      element.focus();
    }
  }

  function focusFirstElement() {
    var focusable = getFocusable();
    focusElement(focusable[0] || dialog);
  }

  function focusLastElement() {
    var focusable = getFocusable();
    focusElement(focusable[focusable.length - 1] || dialog);
  }

  function setBackgroundHidden(isHidden) {
    if (!backdrop || !backdrop.parentNode) return;

    if (isHidden) {
      if (hiddenSiblings.length) setBackgroundHidden(false);

      hiddenSiblings = [];
      var visibleBranch = backdrop;
      var parent = backdrop.parentNode;

      while (parent && parent !== document.documentElement) {
        var siblings = parent.children;
        for (var index = 0; index < siblings.length; index++) {
          var node = siblings[index];
          if (node === visibleBranch) continue;

          hiddenSiblings.push({
            node: node,
            ariaHidden: node.getAttribute('aria-hidden'),
            inert: node.hasAttribute('inert')
          });

          node.setAttribute('aria-hidden', 'true');
          node.setAttribute('inert', '');
        }

        if (parent === document.body) break;
        visibleBranch = parent;
        parent = parent.parentElement;
      }
      return;
    }

    for (var i = 0; i < hiddenSiblings.length; i++) {
      var item = hiddenSiblings[i];
      if (!item || !item.node) continue;

      if (item.ariaHidden === null) item.node.removeAttribute('aria-hidden');
      else item.node.setAttribute('aria-hidden', item.ariaHidden);

      if (!item.inert) item.node.removeAttribute('inert');
    }

    hiddenSiblings = [];
  }

  function keepFocusInside(e) {
    if (!dialog || !backdrop || !backdrop.classList.contains('wmp-modal-backdrop--visible')) return;
    if (dialog.contains(e.target)) return;

    focusFirstElement();
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusable = getFocusable();
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        focusElement(last);
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        focusElement(first);
      }
    }
  }

  /* -----------------------------------------------------------------------
     Keyboard handler
     ----------------------------------------------------------------------- */
  function onKeyDown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    trapFocus(e);
  }

  /* -----------------------------------------------------------------------
     open({ title, content, size, onClose })
     size: 'sm' (400px) | 'md' (560px, default) | 'lg' (720px)
     initialFocus: 'close' | CSS selector | Element
     content: HTML string or DOM element
     ----------------------------------------------------------------------- */
  function open(opts) {
    opts = opts || {};
    build();
    if (!backdrop) return;

    // Reset per-open classes first so previous modal customizations do not leak.
    backdrop.className = 'wmp-modal-backdrop';

    // Size class
    dialog.className = 'wmp-modal-dialog';
    if (opts.size === 'sm') dialog.classList.add('wmp-modal-dialog--sm');
    else if (opts.size === 'lg') dialog.classList.add('wmp-modal-dialog--lg');

    // Optional custom classes for per-modal styling hooks.
    if (opts.backdropClass) backdrop.classList.add(opts.backdropClass);
    if (opts.dialogClass) dialog.classList.add(opts.dialogClass);

    // Title
    if (opts.title) {
      titleEl.textContent = opts.title;
      titleEl.style.display = '';
      dialog.setAttribute('aria-labelledby', titleEl.id);
    } else {
      titleEl.textContent = '';
      titleEl.style.display = 'none';
      dialog.removeAttribute('aria-labelledby');
    }

    // Content
    contentEl.innerHTML = '';
    if (typeof opts.content === 'string') {
      // The i18n strings round-trip through Liquid's `| json` which
      // HTML-escapes `<` and `>`, so by the time we read them back from
      // the <script type="application/json"> they contain literal &lt;h4&gt;
      // etc. Decode entities so innerHTML renders real tags.
      var decoder = document.createElement('textarea');
      decoder.innerHTML = opts.content;
      contentEl.innerHTML = decoder.value;
    } else if (opts.content && opts.content.nodeType) {
      contentEl.appendChild(opts.content);
    }

    if (contentEl.textContent && contentEl.textContent.trim()) {
      dialog.setAttribute('aria-describedby', contentEl.id);
    } else {
      dialog.removeAttribute('aria-describedby');
    }

    primaryActionCb = opts.primaryAction || null;
    if (opts.primaryActionText) {
      primaryBtn.textContent = opts.primaryActionText;
      actionsEl.hidden = false;
    } else {
      primaryBtn.textContent = '';
      actionsEl.hidden = true;
    }

    onCloseCb = opts.onClose || null;

    // Save previous focus
    previousFocus = document.activeElement;

    // Show
    setBackgroundHidden(true);
    backdrop.setAttribute('aria-hidden', 'false');
    backdrop.classList.add('wmp-modal-backdrop--visible');
    document.body.style.overflow = 'hidden';

    // Focus the dialog shell first so screen readers announce the modal
    // context before users encounter individual controls.
    requestAnimationFrame(function () {
      var focusTarget = null;

      if (opts.initialFocus === 'close') {
        focusTarget = closeBtn;
      } else if (typeof opts.initialFocus === 'string') {
        focusTarget = dialog.querySelector(opts.initialFocus);
      } else if (opts.initialFocus && opts.initialFocus.nodeType) {
        focusTarget = opts.initialFocus;
      }

      if (!focusTarget) {
        focusTarget = titleEl && titleEl.style.display !== 'none' ? titleEl : dialog;
      }

      focusElement(focusTarget);
    });

    // Bind ESC + focus trap
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', keepFocusInside);
  }

  /* -----------------------------------------------------------------------
     close()
     ----------------------------------------------------------------------- */
  function close() {
    if (!backdrop) return;

    backdrop.classList.remove('wmp-modal-backdrop--visible');
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setBackgroundHidden(false);

    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('focusin', keepFocusInside);

    // Restore focus
    if (previousFocus && previousFocus.focus) {
      focusElement(previousFocus);
    }
    previousFocus = null;

    if (onCloseCb) {
      onCloseCb();
      onCloseCb = null;
    }
    primaryActionCb = null;
  }

  /* -----------------------------------------------------------------------
     Public API
     ----------------------------------------------------------------------- */
  window.WmpModal = {
    open: open,
    close: close
  };
})();
